/**
 * Reservation groups endpoints for Mews Booking Engine API
 *
 * Step 4.2: Reservation Creation
 * Step 4.3: Final Price Verification
 * Step 4.5: Get reservation group (confirmation)
 *
 * Implements:
 * - createReservationGroup() - Create a group of one or more reservations
 * - getReservationPrice() - Get final price for reservations (before payment)
 * - getReservationGroup() - Fetch reservation group (e.g. after 3DS return)
 *
 * @see MEWS_API_IMPLEMENTATION_PLAN.md - Step 4.2, 4.3, 4.5
 * @see reservation-groups.md - Create reservation group, Get reservation group
 * @see reservations.md - Get reservation price
 */

import { getApiClient } from './client';
import type {
  ApiResponse,
  CreateReservationGroupRequest,
  CreateReservationGroupResponse,
  GetReservationGroupRequest,
  GetReservationGroupResponse,
  MewsApiError,
  MewsCustomer,
  CreateReservationGroupReservationData,
  CreateReservationGroupCreditCardData,
  MewsApiErrorDetailsWithExceeding,
  GetReservationPriceRequest,
  GetReservationPriceResponse,
  GetReservationPriceReservationRequest,
  GetReservationPriceReservationResponse,
} from './types';

/**
 * Distributor API success body is **`{ "ReservationPrice": [ ... ] }`** (not a top-level array).
 * @see docs/room booking page/mews-source/booking-engine-api/operations/reservations.md — *Get reservation price* → **Response**
 * @see docs/room booking page/sample json/multi room getReservationPrice.json
 */
function parseGetReservationPriceRows(
  data: unknown,
): GetReservationPriceReservationResponse[] | undefined {
  if (data == null) return undefined;
  if (Array.isArray(data)) return data as GetReservationPriceReservationResponse[];

  if (typeof data === 'object' && data !== null) {
    const o = data as Record<string, unknown>;
    const direct = o.ReservationPrice ?? o.reservationPrice;
    if (Array.isArray(direct)) return direct as GetReservationPriceReservationResponse[];

    // One-level proxy wrappers (e.g. { data: { ReservationPrice: [...] } })
    for (const key of ['data', 'body', 'result', 'payload'] as const) {
      const inner = o[key];
      if (inner && typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
        const io = inner as Record<string, unknown>;
        const rp = io.ReservationPrice ?? io.reservationPrice;
        if (Array.isArray(rp)) return rp as GetReservationPriceReservationResponse[];
      }
    }
  }
  return undefined;
}

/** Parameters for createReservationGroup() */
export interface CreateReservationGroupParams {
  /** Mews Customer (guest) - required */
  customer: MewsCustomer;
  /** Mews Booker - optional, when booking on behalf of someone else */
  booker?: MewsCustomer;
  /** One or more reservations (multi-room supported) */
  reservations: CreateReservationGroupReservationData[];
  /** Optional availability block ID - provide when available from getAvailability */
  availabilityBlockId?: string;
  /** Optional credit card data - required if hotel has payment gateway and paying now */
  creditCardData?: CreateReservationGroupCreditCardData;
  /** Optional override for ConfigurationId (defaults to env) */
  configurationId?: string;
  /** Optional override for HotelId (defaults to env) */
  hotelId?: string;
}

/**
 * Extracts ExceedingReservationIndexes from error Details when availability decreased since check.
 * @see reservation-groups.md - Error response
 */
export function getExceedingReservationIndexes(error: MewsApiError | undefined): number[] | undefined {
  if (!error?.Details || typeof error.Details !== 'object') return undefined;
  const details = error.Details as MewsApiErrorDetailsWithExceeding;
  if (!Array.isArray(details.ExceedingReservationIndexes)) return undefined;
  return details.ExceedingReservationIndexes;
}

/**
 * Creates a reservation group (one or more reservations) with the Mews Booking Engine API.
 *
 * Endpoint: POST /api/distributor/v1/reservationGroups/create
 *
 * On success returns ReservationGroupId, CustomerId, Reservations (with Numbers), PaymentRequestId, PaymentCardId, TotalAmount.
 * Reservations may be created as "Optional" if balance is due and automatic cancellation for optional reservations is enabled; otherwise "Confirmed".
 *
 * On error (e.g. insufficient availability), the response may include ExceedingReservationIndexes (indexes of reservations that are no longer available).
 *
 * @example
 * ```ts
 * const { customer, booker } = mapGuestDetailsToMewsCustomer(guestDetails);
 * const response = await createReservationGroup({
 *   customer,
 *   booker,
 *   reservations: [{
 *     RoomCategoryId: mewsRoomCategoryId,
 *     StartUtc: startUtc,
 *     EndUtc: endUtc,
 *     RateId: mewsRateId,
 *     AdultCount: adults,
 *     ChildCount: children,
 *     ProductIds: selectedProductIds,
 *     Notes: guestDetails.notes,
 *   }],
 *   availabilityBlockId,
 * });
 * if (response.error) {
 *   const indexes = getExceedingReservationIndexes(response.error);
 *   if (indexes?.length) { /* show "availability changed" message *\/ }
 * }
 * ```
 */
export async function createReservationGroup(
  params: CreateReservationGroupParams
): Promise<ApiResponse<CreateReservationGroupResponse>> {
  if (import.meta.env.DEV) {
    console.log('[createReservationGroup] called with params:', {
      customer: params.customer,
      booker: params.booker,
      reservationsCount: params.reservations.length,
      reservations: params.reservations,
    });
  }

  const client = getApiClient();
  const configurationId = params.configurationId ?? client.getConfigurationId();
  const hotelId = params.hotelId ?? client.getHotelId();

  if (!configurationId) {
    if (import.meta.env.DEV) {
      console.warn('[createReservationGroup] early return – missing ConfigurationId');
    }
    return {
      error: {
        Message:
          'ConfigurationId is required for reservation creation. Set VITE_MEWS_CONFIGURATION_ID or pass configurationId.',
        Code: 'MISSING_CONFIGURATION_ID',
      },
      status: 400,
    };
  }

  if (!hotelId) {
    if (import.meta.env.DEV) {
      console.warn('[createReservationGroup] early return – missing HotelId');
    }
    return {
      error: {
        Message:
          'HotelId is required for reservation creation. Set VITE_MEWS_HOTEL_ID or pass hotelId.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  const requestBody: CreateReservationGroupRequest = {
    Client: client.getClientIdentifier(),
    ConfigurationId: configurationId,
    HotelId: hotelId,
    Customer: params.customer,
    Reservations: params.reservations,
  };

  if (params.booker) {
    requestBody.Booker = params.booker;
  }
  if (params.availabilityBlockId) {
    requestBody.AvailabilityBlockId = params.availabilityBlockId;
  }
  if (params.creditCardData) {
    requestBody.CreditCardData = params.creditCardData;
  }

  if (import.meta.env.DEV) {
    console.log('[createReservationGroup] API call – request:', {
      endpoint: '/api/distributor/v1/reservationGroups/create',
      ...requestBody,
    });
  }

  const result = await client.post<CreateReservationGroupResponse>(
    '/api/distributor/v1/reservationGroups/create',
    requestBody as unknown as Record<string, unknown>
  );

  if (import.meta.env.DEV) {
    if (result.error) {
      console.log('[createReservationGroup] API call – error response:', {
        status: result.status,
        error: result.error,
        ExceedingReservationIndexes: getExceedingReservationIndexes(result.error),
      });
    } else {
      console.log('[createReservationGroup] API call – success response:', {
        status: result.status,
        data: result.data,
      });
    }
  }

  return result;
}

// --- Step 4.3: Final price verification ---

/** Parameters for getReservationPrice() */
export interface GetReservationPriceParams {
  /** One or more reservations (each with Identifier and OccupancyData) */
  reservations: GetReservationPriceReservationRequest[];
  /** ISO 4217 currency code (optional; enterprise default used if omitted) */
  currencyCode?: string;
  /** Optional override for ConfigurationId (defaults to env) */
  configurationId?: string;
}

/**
 * Gets the final price for the given reservations (before payment).
 *
 * Endpoint: POST /api/distributor/v1/reservations/price
 *
 * Use before payment to verify final price and compare with quoted price.
 * Response includes TotalAmount with tax breakdown (Breakdown.Items) and optional
 * AmountToChargeOnConfirmation for the payment gateway.
 *
 * **`CurrencyCode`:** Always **CHF** by default (enterprise settlement / checkout). Override only
 * for diagnostics; browse-time display currency is **not** sent here and does not replace charge currency.
 *
 * ProductIds: Include selected add-on product IDs in each reservation so MEWS
 * includes them in the price. Use base+relative IDs for variable-count (massage,
 * sauna); single ID for once (romance) and per-person-per-night (dinner). MEWS
 * applies ChargingMode (Once, PerPersonPerTimeUnit, etc.) per product.
 *
 * @see MEWS_API_IMPLEMENTATION_PLAN.md - Step 4.3
 * @see docs/room booking page/mews-source/booking-engine-api/operations/reservations.md — *Get reservation price* (request/response contract)
 *
 * **Response shape:** Mews returns JSON `{ "ReservationPrice": [ … ] }`. This function unwraps that
 * to a plain array so callers match each row to `Identifier` from the request (e.g. multi-room `sr-0`, `sr-1`).
 */
export async function getReservationPrice(
  params: GetReservationPriceParams
): Promise<ApiResponse<GetReservationPriceResponse>> {
  if (import.meta.env.DEV) {
    console.log('[getReservationPrice] called with params:', {
      reservationsCount: params.reservations.length,
      currencyCode: params.currencyCode,
    });
  }

  const client = getApiClient();
  const configurationId = params.configurationId ?? client.getConfigurationId();

  if (!configurationId) {
    if (import.meta.env.DEV) {
      console.warn('[getReservationPrice] early return – missing ConfigurationId');
    }
    return {
      error: {
        Message:
          'ConfigurationId is required for reservation pricing. Set VITE_MEWS_CONFIGURATION_ID or pass configurationId.',
        Code: 'MISSING_CONFIGURATION_ID',
      },
      status: 400,
    };
  }

  const requestBody: GetReservationPriceRequest = {
    Client: client.getClientIdentifier(),
    ConfigurationId: configurationId,
    Reservations: params.reservations,
    CurrencyCode: params.currencyCode ?? 'CHF',
  };

  if (import.meta.env.DEV) {
    console.log('[getReservationPrice] API call – request:', {
      endpoint: '/api/distributor/v1/reservations/price',
      ...requestBody,
    });
  }

  const result = await client.post<unknown>(
    '/api/distributor/v1/reservations/price',
    requestBody as unknown as Record<string, unknown>
  );

  if (import.meta.env.DEV) {
    if (result.error) {
      console.log('[getReservationPrice] API call – error response:', {
        status: result.status,
        error: result.error,
      });
    } else {
      console.log('[getReservationPrice] API call – success response:', {
        status: result.status,
        data: result.data,
      });
    }
  }

  if (result.error) return result as ApiResponse<GetReservationPriceResponse>;

  const rows = parseGetReservationPriceRows(result.data);
  if (rows === undefined && import.meta.env.DEV) {
    console.warn(
      "[getReservationPrice] Response JSON was not a ReservationPrice array or { ReservationPrice: [] } — see mews-source/.../reservations.md",
      result.data,
    );
  }
  return {
    ...result,
    data: rows,
  } as ApiResponse<GetReservationPriceResponse>;
}

// --- Step 4.5: Get reservation group (confirmation) ---

/** Parameters for getReservationGroup() */
export interface GetReservationGroupParams {
  /** Unique identifier of the reservation group (from create response or return URL) */
  reservationGroupId: string;
  /** Optional extent for PaymentRequests / Payments (default: minimal for confirmation) */
  extent?: { PaymentRequests?: boolean; Payments?: boolean };
  /** Optional override for HotelId (defaults to env) */
  hotelId?: string;
}

/**
 * Fetches details of the specified reservation group.
 *
 * Endpoint: POST /api/distributor/v1/reservationGroups/get
 *
 * Use after Option B authorization (on-site or after 3DS return) to display confirmation.
 *
 * @see MEWS_API_IMPLEMENTATION_PLAN.md - Step 4.5
 * @see reservation-groups.md - Get reservation group
 */
export async function getReservationGroup(
  params: GetReservationGroupParams
): Promise<ApiResponse<GetReservationGroupResponse>> {
  if (import.meta.env.DEV) {
    console.log('[getReservationGroup] called with params:', {
      reservationGroupId: params.reservationGroupId,
    });
  }

  const client = getApiClient();
  const hotelId = params.hotelId ?? client.getHotelId();

  if (!hotelId) {
    if (import.meta.env.DEV) {
      console.warn('[getReservationGroup] early return – missing HotelId');
    }
    return {
      error: {
        Message:
          'HotelId is required. Set VITE_MEWS_HOTEL_ID or pass hotelId.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  const requestBody: GetReservationGroupRequest = {
    Client: client.getClientIdentifier(),
    HotelId: hotelId,
    ReservationGroupId: params.reservationGroupId,
  };
  if (params.extent) {
    requestBody.Extent = params.extent;
  }

  if (import.meta.env.DEV) {
    console.log('[getReservationGroup] API call – request:', {
      endpoint: '/api/distributor/v1/reservationGroups/get',
      ...requestBody,
    });
  }

  const result = await client.post<GetReservationGroupResponse>(
    '/api/distributor/v1/reservationGroups/get',
    requestBody as unknown as Record<string, unknown>
  );

  if (import.meta.env.DEV) {
    if (result.error) {
      console.log('[getReservationGroup] API call – error response:', {
        status: result.status,
        error: result.error,
      });
    } else {
      console.log('[getReservationGroup] API call – success response:', {
        status: result.status,
        data: result.data,
      });
    }
  }

  return result;
}
